import { useEffect } from 'react'
import { Navigate, useLocation, useRoutes } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import Layout from '@/components/Layout'
import LoginPage from '@/pages/LoginPage'
import DashboardPage from '@/pages/DashboardPage'
import SolicitacoesPage from '@/pages/SolicitacoesPage'
import NovaSolicitacaoPage from '@/pages/NovaSolicitacaoPage'
import EditarSolicitacaoPage from '@/pages/EditarSolicitacaoPage'
import VisualizarSolicitacao from '@/pages/VisualizarSolicitacao'
import ConfiguracoesPage from '@/pages/ConfiguracoesPage'
import RelatoriosPage from '@/pages/RelatoriosPage'
import UsuariosPage from '@/pages/UsuariosPage'
import CatadoresPage from '@/pages/CatadoresPage'
import SolicitacoesOnlinePage from '@/pages/SolicitacoesOnlinePage'
import PortalCidadaoPage from '@/pages/PortalCidadaoPage'
import ReplantioPage from '@/pages/ReplantioPage'
import { bootstrapAdminMaster } from '@/utils/bootstrapAdmin'

// Re-exporta constantes para LoginPage e UsuariosPage
export { ADMIN_USERNAME } from '@/utils/bootstrapAdmin'

// ── Spinner de inicialização ──────────────────────────────────────────────
function AppLoading() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#0f3b50', borderTopColor: 'transparent' }} />
        <p className="text-sm text-gray-500">Carregando...</p>
      </div>
    </div>
  )
}

// ── Proteção de rotas ────────────────────────────────────────────────────
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()

  // Aguarda o SDK confirmar o estado da sessão antes de redirecionar
  if (loading) return <AppLoading />

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  return <>{children}</>
}

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  )
}

// ── Definição de rotas ───────────────────────────────────────────────────
export const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/bem-vindo',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      { index: true,                        element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard',                  element: <DashboardPage /> },
      { path: 'solicitacoes',               element: <SolicitacoesPage /> },
      { path: 'solicitacoes/nova',          element: <NovaSolicitacaoPage /> },
      { path: 'solicitacoes/:id',           element: <VisualizarSolicitacao /> },
      { path: 'solicitacoes/:id/editar',    element: <EditarSolicitacaoPage /> },
      { path: 'configuracoes',              element: <ConfiguracoesPage /> },
      { path: 'relatorios',                 element: <RelatoriosPage /> },
      { path: 'usuarios',                   element: <UsuariosPage /> },
      { path: 'catadores',                  element: <CatadoresPage /> },
      { path: 'replantio',                   element: <ReplantioPage /> },
      { path: 'solicitacoes-online',         element: <SolicitacoesOnlinePage /> },
      { path: '*',                          element: <Navigate to="/dashboard" replace /> },
    ],
  },
  {
    path: '/portal-cidadao',
    element: <PortalCidadaoPage />,
  },
]

// ── Componente raiz ──────────────────────────────────────────────────────
export default function App() {
  useEffect(() => {
    // Garante a existência do Admin Master no banco ao iniciar a app
    bootstrapAdminMaster()
  }, [])

  const element = useRoutes(routes)
  return element
}
